import { PUBLIC_ROUTES } from "@lib/routePaths";

// Public, non-member pages served by CloudFront outside of /members/*.
//
// The literals live in routePaths.ts (shared with the route tree); this list
// picks the public ROOTS and is the single source of truth for two related,
// must-stay-in-sync decisions:
//   1. BrowserRouter basename — these paths render under "/", everything else
//      under "/members" (see getBasename in App.tsx).
//   2. Segment source — these paths send their analytics to the checkout /
//      legacy Segment source (the same source the checkout backend, omg-api,
//      and the legacy omgyes-server checkout write to); member pages use the
//      New Member Home source (see segmentInstance.ts). NOTE: the source
//      decision is NOT PUBLIC_PATHS alone — it's isCheckoutSourcePath (below),
//      a superset that also covers the /members/checkout/pf PF variant.
export const PUBLIC_PATHS = [
  PUBLIC_ROUTES.GIFTS,
  PUBLIC_ROUTES.REDEEM,
  PUBLIC_ROUTES.CHECKOUT,
] as const;

// True for a public path itself or any nested path under it. The trailing-slash
// guard avoids matching siblings like "/giftsfoo".
//
// Also matches locale-prefixed gift/redeem URLs (e.g. /de/gifts, /es_419/redeem,
// /de/gifts/card) from the marketing footer and older gift emails. These must
// resolve under basename "/" so the /:lng/* redirect routes actually mount —
// otherwise getBasename() returns "/members", the router matches nothing for a
// "/de/gifts" browser path, and <Routes> renders null (blank page, OMG-2064).
// The redirect immediately rewrites them to the canonical /gifts?lng= path.
export const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATHS.some(
    (publicPath) =>
      pathname === publicPath || pathname.startsWith(`${publicPath}/`),
  ) || /^\/[^/]+\/(gifts|redeem)(\/|$)/.test(pathname);

// The payment-first checkout variant (PUBLIC_ROUTES.CHECKOUT_PAYMENT_FIRST =
// "/checkout/pf") renders at TWO browser paths: "/checkout/pf" (prod entry —
// already public via startsWith("/checkout/")) and "/members/checkout/pf"
// (direct UAT/foxhole URLs — rendered under the "/members" basename).
//
// Composed from the route constant rather than hardcoded. The old comment
// justified the literal as keeping this a dependency-free low-level module, and
// that rationale went stale the moment the file started importing routePaths.ts
// (#950): routePaths.ts is itself dependency-free, which is the reason it exists.
// What still holds is the narrower point the old comment was reaching for — do
// NOT import this from paymentFirst.ts, which pulls in the Convert config graph
// and cannot be loaded at the segmentInstance bootstrap.
const MEMBER_BASENAME_PF_CHECKOUT = `/members${PUBLIC_ROUTES.CHECKOUT_PAYMENT_FIRST}`;

// Segment-source decision ONLY (distinct from isPublicPath, which also drives
// the router basename in App.tsx — must stay unchanged so /members/checkout/pf
// keeps rendering under /members). Checkout events must reach the checkout/legacy
// source (revenue destinations, no PII scrub) regardless of which URL form the
// variant was reached at — otherwise the /members form's ecommerce props are
// stripped by the member-source allowlist (OMG-2120).
export const isCheckoutSourcePath = (pathname: string): boolean =>
  isPublicPath(pathname) ||
  pathname === MEMBER_BASENAME_PF_CHECKOUT ||
  pathname.startsWith(`${MEMBER_BASENAME_PF_CHECKOUT}/`);
